import { useState } from "react";
import { fieldValue } from "./form.js";
import { signIn, signOut, type SecondStepChallenge, type Session } from "./auth.js";
import { SecondStepForm } from "./SecondStep.js";
import { elevate } from "./tablet.js";

/**
 * A full sign-in for one fenced action on a tablet (#80, D6 mitigation 3):
 * a big order, a cash-up, a price, a role — whichever the organisation
 * chose. The password signs in for five minutes, held in memory only, and
 * the stored sign-in is cleared at once so the tablet does not stay in that
 * person's account. Then they do the action again.
 */
export function StepUp({ onClose }: { readonly onClose: (done: boolean) => void }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Somebody with two-step sign-in set up is asked for their code too (#92).
  const [secondStep, setSecondStep] = useState<SecondStepChallenge | null>(null);

  const finish = (session: Session) => {
    elevate(session.accessToken);
    // The library keeps the sign-in in local storage; a shared tablet must not.
    signOut();
    onClose(true);
  };

  if (secondStep) {
    return (
      <div className="lock lock-overlay" role="dialog" aria-label="Sign in for this action">
        <div className="lock-card">
          <div className="label">Needs a full sign-in</div>
          <div className="lock-title" style={{ fontSize: 26, marginBottom: 12 }}>Your code</div>
          <SecondStepForm
            challenge={secondStep}
            onSignedIn={finish}
            onCancel={() => {
              setSecondStep(null);
              setError(null);
            }}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="lock lock-overlay" role="dialog" aria-label="Sign in for this action">
      <form
        className="lock-card"
        onSubmit={async (e) => {
          e.preventDefault();
          // The boxes, not the state: autofill fills one without React
          // hearing, and the empty state reached Cognito (form.ts).
          const typed = fieldValue(e.currentTarget, "email", email).trim();
          const secret = fieldValue(e.currentTarget, "password", password);
          setEmail(typed);
          setPassword(secret);
          setBusy(true);
          setError(null);
          try {
            const result = await signIn(typed, secret);
            if (result.kind === "second-step") return setSecondStep(result.challenge);
            if (result.kind !== "session") {
              throw new Error("Finish setting up your password on your own device first, then try again here.");
            }
            finish(result.session);
          } catch (err) {
            setError(err instanceof Error ? err.message : "That did not sign in");
          } finally {
            setBusy(false);
            setPassword("");
          }
        }}
      >
        <div className="label">Needs a full sign-in</div>
        <div className="lock-title" style={{ fontSize: 26 }}>Sign in to carry on</div>
        <div className="muted" style={{ fontSize: 14, lineHeight: 1.5, margin: "6px 0 14px" }}>
          This organisation asks for a password, not just a PIN, for that. Signed in, you have five minutes to do
          it again; then the tablet goes back to PINs.
        </div>
        <label className="field">
          <span>Email</span>
          <input type="email" name="email" autoComplete="username" value={email} onChange={(e) => setEmail(e.target.value)} required />
        </label>
        <label className="field" style={{ marginTop: 10 }}>
          <span>Password</span>
          <input type="password" name="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} required />
        </label>
        {error && <div className="msg err" style={{ marginTop: 12 }}>{error}</div>}
        <div className="row" style={{ marginTop: 14 }}>
          <button className="btn" type="button" onClick={() => onClose(false)}>Cancel</button>
          <span className="spacer" />
          <button className="btn primary" type="submit" disabled={busy}>{busy ? "Signing in…" : "Sign in"}</button>
        </div>
      </form>
    </div>
  );
}
